import { StoredLogo } from '../components/shared/StoredLogo';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { clientsService, sitesService, servicesService, sitePricingService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { getApiError } from '@/lib/utils';
import { useDebounce } from '../hooks/usePerformance';
import IndependentCustomerDialog from '../components/IndependentCustomerDialog';
import SharedSiteEditDialog from '../components/SharedSiteEditDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { EmailPreview } from '@/components/ui/email-preview';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Separator } from '@/components/ui/separator';
import BudgetHistoryPanel from '../components/BudgetHistoryPanel';
import { 
  Plus, Loader2, Pencil, Trash2, Building2, MapPin, 
  ChevronLeft, Phone, Mail, User, ArrowRight, Link2, Unlink,
  Search, ChevronDown, ChevronRight, Users, Star, Banknote, X, Save, Edit3, Eye,
  Upload, ImageIcon, Layers, AlertCircle
} from 'lucide-react';

// Preset colours for quick selection
const presetColours = [
  { name: 'Sky Blue', hex: '#0EA5E9' },
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Indigo', hex: '#6366F1' },
  { name: 'Navy', hex: '#1E3A8A' },
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Green', hex: '#22C55E' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Forest', hex: '#166534' },
  { name: 'Amber', hex: '#F59E0B' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Rose', hex: '#E11D48' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Violet', hex: '#7C3AED' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Fuchsia', hex: '#D946EF' },
  { name: 'Slate', hex: '#64748B' },
  { name: 'Grey', hex: '#6B7280' },
  { name: 'Zinc', hex: '#71717A' },
  { name: 'Stone', hex: '#78716C' },
  { name: 'Cyan', hex: '#06B6D4' },
  { name: 'Lime', hex: '#84CC16' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Coral', hex: '#FF6B6B' },
  { name: 'Plum', hex: '#9333EA' },
];

const countries = ['United Kingdom', 'United Arab Emirates'];

export default function CustomersPage() {
  const { isAdmin, canManageSites, canManageLogos, canViewSites, canEditSiteAuditorFields, isAuditor, isScheduler } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { formatCurrency, currencySymbol, region, settings } = useSettings();
  const phonePlaceholder = region === 'UAE' ? '+971 50 123 4567' : '+44 7700 900000';
  
  // Auditor-only: can view customer data but only edit specific fields
  // (contacts, budget cost of sales %, stockholding period)
  const isAuditorOnly = isAuditor && !isScheduler && !isAdmin;
  
  // Auditors cannot see or edit pricing - only admin and schedulers can
  const canEditPricing = isAdmin || canManageSites;
  
  // Customer list state
  const [clients, setClients] = useState([]);
  const [groupClients, setGroupClients] = useState([]);
  const [usedColours, setUsedColours] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'inactive'
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedGroupSites, setExpandedGroupSites] = useState({});
  const [loadingGroupSites, setLoadingGroupSites] = useState(new Set());
  
  // Customer Edit Dialog state (unified)
  const [customerEditDialogOpen, setCustomerEditDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({});
  const [contacts, setContacts] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitePricing, setSitePricing] = useState({});
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingEditData, setLoadingEditData] = useState(false);

  // D3 — AP site exclusions (Scheduler/Admin only)
  const [apExclusions, setApExclusions] = useState([]);
  const [apExclusionsLoading, setApExclusionsLoading] = useState(false);
  const [apExclusionSiteId, setApExclusionSiteId] = useState('');

  // D3 — AP rollout mode + included-site management
  // 'all_except'    — (default) all sites eligible minus exclusions
  // 'selected_only' — only explicitly ticked sites are eligible
  const [apRolloutMode, setApRolloutMode] = useState('all_except');
  const [apInclusions, setApInclusions]   = useState([]);
  const [apInclusionsLoading, setApInclusionsLoading] = useState(false);
  const [apSiteSearch, setApSiteSearch]   = useState('');
  // NOTE: Per-client AP threshold overrides have been moved to SettingsPage.js
  
  // Independent Customer Dialog state (simplified view)
  const [independentDialogOpen, setIndependentDialogOpen] = useState(false);
  const [independentCustomer, setIndependentCustomer] = useState(null);
  
  // Dialog states for inline editing
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', role: '', is_primary: false });
  
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [selectedSiteForPricing, setSelectedSiteForPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({ service_id: '', price: '', currency: 'GBP', is_default: false, description: '' });
  
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState({});
  const [siteContacts, setSiteContacts] = useState([]);
  const [siteContactDialogOpen, setSiteContactDialogOpen] = useState(false);
  const [editingSiteContact, setEditingSiteContact] = useState(null);
  const [siteContactForm, setSiteContactForm] = useState({ name: '', email: '', phone: '', role: '', for_job_scheduling: false, for_job_reports: false });
  const [siteDialogTab, setSiteDialogTab] = useState('profile');
  
  // Delete dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Batch selection state
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [selectedSites, setSelectedSites] = useState(new Set());
  const [batchActionDialogOpen, setBatchActionDialogOpen] = useState(false);
  const [batchActionType, setBatchActionType] = useState(null); // 'status' or 'frequency'
  const [batchActionValue, setBatchActionValue] = useState(null);
  const [batchCustomDays, setBatchCustomDays] = useState('');
  const [batchProcessing, setBatchProcessing] = useState(false);
  
  // Create customer dialog (for new customers)
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: '',
    client_kind: 'independent',
    colour: null,
    address_line_1: '',
    address_line_2: '',
    city: '',
    postcode: '',
    country: region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom',
    forecast_price: '',
    audit_frequency: ''
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  // Attach to Group dialog
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [attaching, setAttaching] = useState(false);

  // Convert to Group dialog
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  // Color confirmation dialog state
  const [colorConfirmDialogOpen, setColorConfirmDialogOpen] = useState(false);
  const [pendingColour, setPendingColour] = useState(null);
  
  // Site Email Script Dialog state
  const [siteEmailScriptDialogOpen, setSiteEmailScriptDialogOpen] = useState(false);
  const [tempSiteEmailScript, setTempSiteEmailScript] = useState('');
  const [showSiteEmailPreview, setShowSiteEmailPreview] = useState(false);
  const [colourInUseBy, setColourInUseBy] = useState(null);
  const [colourTargetForm, setColourTargetForm] = useState(null); // 'customer' | 'new'

  // Load all clients
  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const data = await clientsService.list({ region });
      setClients(data);
      setUsedColours(data.filter(c => c.colour).map(c => c.colour));
      const groups = await clientsService.listGroups({ region });
      setGroupClients(groups);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load customers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, region]);

  const loadServices = useCallback(async () => {
    try {
      const data = await servicesService.list();
      setServices(data);
    } catch (err) {
      console.error('Failed to load services:', err);
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadServices();
  }, [loadClients, loadServices]);

  // D3 — Load AP site exclusions for a client (Scheduler/Admin only)
  const loadApExclusions = useCallback(async (clientId) => {
    if (!clientId || (!isAdmin && !isScheduler)) return;
    setApExclusionsLoading(true);
    try {
      const res = await api.get(`/action-plans/admin/site-exclusions?client_id=${clientId}`);
      setApExclusions(res.data || []);
    } catch (err) {
      setApExclusions([]);
    } finally {
      setApExclusionsLoading(false);
    }
  }, [isAdmin, isScheduler]);

  const addApExclusion = async (clientId, siteId) => {
    if (!siteId) return;
    try {
      await api.post('/action-plans/admin/site-exclusions', { client_id: clientId, site_id: Number(siteId) });
      await loadApExclusions(clientId);
      setApExclusionSiteId('');
      toast({ title: 'Site excluded from AP', description: 'This site will not be subject to AP requirements.' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to add exclusion', variant: 'destructive' });
    }
  };

  const removeApExclusion = async (clientId, exclusionId) => {
    try {
      await api.delete(`/action-plans/admin/site-exclusions/${exclusionId}`);
      await loadApExclusions(clientId);
      toast({ title: 'Exclusion removed', description: 'Site is now AP-applicable.' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to remove exclusion', variant: 'destructive' });
    }
  };

  // D3 — AP rollout mode + included-site helpers
  const loadApInclusions = useCallback(async (clientId) => {
    if (!clientId || (!isAdmin && !isScheduler)) return;
    setApInclusionsLoading(true);
    try {
      const res = await api.get(`/action-plans/admin/site-inclusions?client_id=${clientId}`);
      setApInclusions(res.data || []);
    } catch {
      setApInclusions([]);
    } finally {
      setApInclusionsLoading(false);
    }
  }, [isAdmin, isScheduler]);

  const saveApRolloutMode = async (clientId, mode) => {
    try {
      await api.patch(`/action-plans/admin/client-ap-rollout-mode/${clientId}`, { ap_rollout_mode: mode });
    } catch {
      toast({ title: 'Error', description: 'Failed to save rollout mode', variant: 'destructive' });
    }
  };

  const addApInclusion = async (clientId, siteId) => {
    if (!siteId) return;
    try {
      await api.post('/action-plans/admin/site-inclusions', { client_id: clientId, site_id: Number(siteId) });
      await loadApInclusions(clientId);
    } catch {
      toast({ title: 'Error', description: 'Failed to include site', variant: 'destructive' });
    }
  };

  const removeApInclusion = async (clientId, inclusionId) => {
    try {
      await api.delete(`/action-plans/admin/site-inclusions/${inclusionId}`);
      await loadApInclusions(clientId);
    } catch {
      toast({ title: 'Error', description: 'Failed to remove inclusion', variant: 'destructive' });
    }
  };

  // Load customer data for editing
  const loadCustomerForEdit = useCallback(async (client) => {
    // Normalise client_kind to lowercase so all downstream === 'group' /
    // === 'independent' comparisons work regardless of how the DB stored it.
    const normClient = { ...client, client_kind: client.client_kind?.toLowerCase?.() ?? client.client_kind };

    // For Independent customers, use the simplified dialog
    if (normClient.client_kind === 'independent') {
      setIndependentCustomer(normClient);
      setIndependentDialogOpen(true);
      return;
    }
    
    // For Group customers, use the full dialog
    setEditingCustomer(normClient);
    setCustomerEditDialogOpen(true);
    setLoadingEditData(true);
    try {
      // Set basic customer form
      setCustomerForm({
        name: normClient.name || '',
        client_kind: normClient.client_kind,
        colour: normClient.colour,
        is_active: normClient.is_active !== false,
        address_line_1: normClient.address_line_1 || '',
        address_line_2: normClient.address_line_2 || '',
        city: normClient.city || '',
        postcode: normClient.postcode || '',
        country: normClient.country || 'United Kingdom',
        // D3 — AP applicability (building control does NOT roll out AP to live clients)
        ap_applicability_enabled: normClient.ap_applicability_enabled ?? false,
      });
      
      // Load contacts
      const contactsData = await clientsService.listContacts(normClient.id);
      setContacts(contactsData);
      
      // Load sites for this client (increase limit to get all sites)
      const allSites = await sitesService.list({ region, limit: 500 });
      const clientSites = allSites.filter(site => 
        site.parent_client_id === normClient.id || site.independent_client_id === normClient.id
      );
      setSites(clientSites);
      
      // Load pricing for each site in parallel (batch requests for better performance)
      const pricingBysite = {};
      const batchSize = 10; // Load 10 sites' pricing at a time
      for (let i = 0; i < clientSites.length; i += batchSize) {
        const batch = clientSites.slice(i, i + batchSize);
        const batchResults = await Promise.all(
          batch.map(async (site) => {
            try {
              const pricing = await sitePricingService.getForSite(site.id);
              return { siteId: site.id, pricing };
            } catch (err) {
              return { siteId: site.id, pricing: [] };
            }
          })
        );
        batchResults.forEach(({ siteId, pricing }) => {
          pricingBysite[siteId] = pricing;
        });
      }
      setSitePricing(pricingBysite);

      // D3 — Load AP rollout config for this client
      const rolloutMode = normClient.ap_rollout_mode || 'all_except';
      setApRolloutMode(rolloutMode);
      if (normClient.ap_applicability_enabled) {
        loadApExclusions(normClient.id);
        if (rolloutMode === 'selected_only') {
          loadApInclusions(normClient.id);
        }
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load customer data', variant: 'destructive' });
    } finally {
      setLoadingEditData(false);
    }
  }, [toast, region, loadApExclusions, loadApInclusions]);

  // Open customer for editing (single click)
  const openCustomerEdit = (client) => {
    loadCustomerForEdit(client);
  };

  // Open site directly for editing (skipping full customer load)
  const openSiteForDirectEdit = async (parentClient, site) => {
    setLoadingEditData(true);
    try {
      // Load just this site's pricing
      let pricing = [];
      try {
        pricing = await sitePricingService.getForSite(site.id);
      } catch (err) {
        pricing = [];
      }
      
      // Set up minimal state to show site edit dialog
      setEditingCustomer(parentClient);
      setSites([site]);
      setSitePricing({ [site.id]: pricing });
      setContacts([]);
      setCustomerForm({
        name: parentClient.name,
        address_line_1: parentClient.address_line_1 || '',
        address_line_2: parentClient.address_line_2 || '',
        city: parentClient.city || '',
        postcode: parentClient.postcode || '',
        country: parentClient.country || 'United Kingdom',
      });
      
      // Open site dialog directly
      openSiteDialog(site);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load site data', variant: 'destructive' });
    } finally {
      setLoadingEditData(false);
    }
  };

  // Close customer edit dialog
  const closeCustomerEditDialog = () => {
    setCustomerEditDialogOpen(false);
    setEditingCustomer(null);
    setCustomerForm({});
    setContacts([]);
    setSites([]);
    setSitePricing({});
    setApExclusions([]);
    setApExclusionSiteId('');
    setApRolloutMode('all_except');
    setApInclusions([]);
    setApSiteSearch('');
  };

  // Save customer changes
  const handleSaveCustomer = async () => {
    if (!customerForm.name?.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const updatedCustomer = await clientsService.update(editingCustomer.id, customerForm);
      toast({ title: 'Success', description: 'Customer updated successfully' });
      
      // Optimistically update local state instead of refetching everything
      setClients(prev => prev.map(c => 
        c.id === editingCustomer.id 
          ? { ...c, ...customerForm, site_names: c.site_names, site_count: c.site_count }
          : c
      ));
      
      // Update the editing customer with the new data
      setEditingCustomer(prev => ({ ...prev, ...customerForm }));
      
      // Also update group clients if this is a group
      if (customerForm.client_kind === 'group') {
        setGroupClients(prev => prev.map(g => 
          g.id === editingCustomer.id ? { ...g, name: customerForm.name } : g
        ));
      }
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Create new customer
  const handleCreateCustomer = async () => {
    if (!newCustomerForm.name?.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    if (!newCustomerForm.address_line_1?.trim()) {
      toast({ title: 'Error', description: 'Address Line 1 is required', variant: 'destructive' });
      return;
    }

    setCreatingCustomer(true);
    try {
      const newClient = await clientsService.create({
        ...newCustomerForm,
        forecast_price: newCustomerForm.forecast_price ? parseFloat(newCustomerForm.forecast_price) : null
      });
      toast({ title: 'Success', description: 'Customer created' });
      setCreateDialogOpen(false);
      setNewCustomerForm({
        name: '',
        client_kind: 'independent',
        colour: null,
        address_line_1: '',
        address_line_2: '',
        city: '',
        postcode: '',
        country: region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom',
        forecast_price: ''
      });
      loadClients();
      // Open the newly created customer for editing
      loadCustomerForEdit(newClient);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to create customer'), variant: 'destructive' });
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Contact CRUD
  const openContactDialog = (contact = null) => {
    setEditingContact(contact);
    setContactForm(contact ? {
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      role: contact.role || '',
      is_primary: contact.is_primary || false
    } : { name: '', email: '', phone: '', role: '', is_primary: false });
    setContactDialogOpen(true);
  };

  const handleSaveContact = async () => {
    if (!contactForm.name?.trim()) {
      toast({ title: 'Error', description: 'Contact name is required', variant: 'destructive' });
      return;
    }

    try {
      if (editingContact) {
        await clientsService.updateContact(editingCustomer.id, editingContact.id, contactForm);
        toast({ title: 'Success', description: 'Contact updated' });
      } else {
        await clientsService.createContact(editingCustomer.id, contactForm);
        toast({ title: 'Success', description: 'Contact added' });
      }
      setContactDialogOpen(false);
      // Reload contacts
      const contactsData = await clientsService.listContacts(editingCustomer.id);
      setContacts(contactsData);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save contact'), variant: 'destructive' });
    }
  };

  const handleDeleteContact = async (contact) => {
    try {
      await clientsService.deleteContact(editingCustomer.id, contact.id);
      toast({ title: 'Success', description: 'Contact deleted' });
      const contactsData = await clientsService.listContacts(editingCustomer.id);
      setContacts(contactsData);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete contact', variant: 'destructive' });
    }
  };

  // Site CRUD (for group clients)
  const openSiteDialog = async (site = null) => {
    setEditingSite(site);
    setSiteDialogTab('profile');
    setSiteForm(site ? {
      name: site.name || '',
      address_line_1: site.address_line_1 || '',
      address_line_2: site.address_line_2 || '',
      city: site.city || '',
      postcode: site.postcode || '',
      country: site.country || (region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom'),
      forecast_price: site.forecast_price?.toString() || '',
      audit_frequency: site.audit_frequency || '',
      custom_frequency_days: site.custom_frequency_days || '',
      year_end_month: site.year_end_month?.toString() || '',
      year_end_day: site.year_end_day?.toString() || '',
      budget_cost_of_sales_pct: site.budget_cost_of_sales_pct || '',
      days_stockholding_period: site.days_stockholding_period || '',
      food_budget_cost_of_sales_pct: site.food_budget_cost_of_sales_pct || '',
      food_days_stockholding_period: site.food_days_stockholding_period || '',
      custom_schedule_email_script: site.custom_schedule_email_script || ''
    } : {
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      postcode: '',
      country: region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom',
      forecast_price: '',
      audit_frequency: '',
      custom_frequency_days: '',
      year_end_month: '',
      year_end_day: '',
      budget_cost_of_sales_pct: '',
      days_stockholding_period: '',
      food_budget_cost_of_sales_pct: '',
      food_days_stockholding_period: '',
      custom_schedule_email_script: ''
    });
    setSiteDialogOpen(true);
    // When editing an existing site the full data is loaded by SharedSiteEditDialog.
    // When adding a new site (site = null) the form is shown inline below.
  };

  const handleSaveSite = async () => {
    if (!siteForm.name?.trim()) {
      toast({ title: 'Error', description: 'Site name is required', variant: 'destructive' });
      return;
    }

    try {
      const siteData = {
        ...siteForm,
        forecast_price: siteForm.forecast_price ? parseFloat(siteForm.forecast_price) : null,
        audit_frequency: siteForm.audit_frequency || null,
        custom_frequency_days: siteForm.audit_frequency === 'custom' && siteForm.custom_frequency_days ? parseInt(siteForm.custom_frequency_days) : null,
        year_end_month: siteForm.year_end_month ? parseInt(siteForm.year_end_month) : null,
        year_end_day: siteForm.year_end_day ? parseInt(siteForm.year_end_day) : null,
        is_active: siteForm.is_active !== false,
        parent_client_id: editingCustomer.client_kind === 'group' ? editingCustomer.id : null,
        // Phase 1A fields — Beverages
        budget_cost_of_sales_pct: siteForm.budget_cost_of_sales_pct ? parseFloat(siteForm.budget_cost_of_sales_pct) : null,
        days_stockholding_period: siteForm.days_stockholding_period ? parseInt(siteForm.days_stockholding_period) : null,
        // Food fields
        food_budget_cost_of_sales_pct: siteForm.food_budget_cost_of_sales_pct ? parseFloat(siteForm.food_budget_cost_of_sales_pct) : null,
        food_days_stockholding_period: siteForm.food_days_stockholding_period ? parseInt(siteForm.food_days_stockholding_period) : null,
        custom_schedule_email_script: siteForm.custom_schedule_email_script || null
      };

      // Add Site only — edit is now handled by SharedSiteEditDialog
      const newSite = await sitesService.create(siteData);
      toast({ title: 'Success', description: 'Site created' });
      
      // Add new site to local state
      setSites(prev => [...prev, newSite]);
      
      // Update site count in clients list
      setClients(prev => prev.map(c => 
        c.id === editingCustomer.id 
          ? { ...c, site_count: (c.site_count || 0) + 1, site_names: [...(c.site_names || []), newSite.name] }
          : c
      ));
      setSiteDialogOpen(false);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save site'), variant: 'destructive' });
    }
  };

  const handleDeleteSite = async (site) => {
    try {
      await sitesService.delete(site.id);
      toast({ title: 'Success', description: 'Site deleted' });
      
      // Optimistically update local state
      setSites(prev => prev.filter(s => s.id !== site.id));
      
      // Update site count in clients list
      setClients(prev => prev.map(c => 
        c.id === editingCustomer.id 
          ? { 
              ...c, 
              site_count: Math.max(0, (c.site_count || 1) - 1),
              site_names: (c.site_names || []).filter(n => n !== site.name)
            }
          : c
      ));
      
      // Update expanded group sites if applicable
      if (editingCustomer.client_kind === 'group') {
        setExpandedGroupSites(prev => ({
          ...prev,
          [editingCustomer.id]: prev[editingCustomer.id]?.filter(s => s.id !== site.id)
        }));
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete site', variant: 'destructive' });
    }
  };

  // Detach Site from Group
  const [detachConfirmOpen, setDetachConfirmOpen] = useState(false);
  const [detaching, setDetaching] = useState(false);

  const handleDetachSite = async () => {
    if (!editingSite) return;
    try {
      setDetaching(true);
      const result = await sitesService.detachFromGroup(editingSite.id);
      toast({ title: 'Site Detached', description: result.message || `"${editingSite.name}" is now an independent customer` });
      setDetachConfirmOpen(false);
      setSiteDialogOpen(false);
      // Refresh data
      loadClients();
      // Clear stale expanded group sites cache for the old group
      if (editingSite?.parent_client_id) {
        setExpandedGroupSites(prev => {
          const next = { ...prev };
          delete next[editingSite.parent_client_id];
          return next;
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to detach site'), variant: 'destructive' });
    } finally {
      setDetaching(false);
    }
  };

  // Site Contact CRUD
  const openSiteContactDialog = (contact = null) => {
    setEditingSiteContact(contact);
    setSiteContactForm(contact ? {
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      role: contact.role || '',
      for_job_scheduling: contact.for_job_scheduling || false,
      for_job_reports: contact.for_job_reports || false
    } : {
      name: '',
      email: '',
      phone: '',
      role: '',
      for_job_scheduling: false,
      for_job_reports: false
    });
    setSiteContactDialogOpen(true);
  };

  const handleSaveSiteContact = async () => {
    if (!siteContactForm.name?.trim()) {
      toast({ title: 'Error', description: 'Contact name is required', variant: 'destructive' });
      return;
    }

    try {
      const data = {
        name: siteContactForm.name,
        email: siteContactForm.email || null,
        phone: siteContactForm.phone || null,
        role: siteContactForm.role || null,
        for_job_scheduling: siteContactForm.for_job_scheduling,
        for_job_reports: siteContactForm.for_job_reports
      };

      if (editingSiteContact) {
        await sitesService.updateContact(editingSite.id, editingSiteContact.id, data);
        toast({ title: 'Success', description: 'Contact updated' });
      } else {
        await sitesService.createContact(editingSite.id, data);
        toast({ title: 'Success', description: 'Contact added' });
      }
      setSiteContactDialogOpen(false);
      // Reload contacts
      const contacts = await sitesService.listContacts(editingSite.id);
      setSiteContacts(contacts);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save contact'), variant: 'destructive' });
    }
  };

  const handleDeleteSiteContact = async (contact) => {
    try {
      await sitesService.deleteContact(editingSite.id, contact.id);
      toast({ title: 'Success', description: 'Contact deleted' });
      const contacts = await sitesService.listContacts(editingSite.id);
      setSiteContacts(contacts);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete contact', variant: 'destructive' });
    }
  };

  // Pricing CRUD
  const openPricingDialog = (site, pricing = null) => {
    setSelectedSiteForPricing(site);
    setEditingPricing(pricing);
    setPricingForm(pricing ? {
      service_id: pricing.service_id?.toString() || '',
      price: pricing.price?.toString() || '',
      currency: pricing.currency || (region === 'UAE' ? 'AED' : 'GBP'),
      is_default: pricing.is_default || false,
      description: pricing.description || ''
    } : {
      service_id: '',
      price: '',
      currency: region === 'UAE' ? 'AED' : 'GBP',
      is_default: false,
      description: ''
    });
    setPricingDialogOpen(true);
  };

  const handleSavePricing = async () => {
    if (!pricingForm.service_id || !pricingForm.price) {
      toast({ title: 'Error', description: 'Service and price are required', variant: 'destructive' });
      return;
    }

    try {
      const data = {
        site_id: selectedSiteForPricing.id,
        service_id: parseInt(pricingForm.service_id),
        price: parseFloat(pricingForm.price),
        currency: pricingForm.currency,
        is_default: pricingForm.is_default,
        description: pricingForm.description || null
      };

      if (editingPricing) {
        await sitePricingService.update(editingPricing.id, data);
        toast({ title: 'Success', description: 'Pricing updated' });
      } else {
        await sitePricingService.create(data);
        toast({ title: 'Success', description: 'Pricing added' });
      }
      setPricingDialogOpen(false);
      // Reload pricing for this site
      const pricing = await sitePricingService.getForSite(selectedSiteForPricing.id);
      setSitePricing(prev => ({ ...prev, [selectedSiteForPricing.id]: pricing }));
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save pricing'), variant: 'destructive' });
    }
  };

  const handleDeletePricing = async (siteId, pricingId) => {
    try {
      await sitePricingService.delete(pricingId);
      toast({ title: 'Success', description: 'Pricing deleted' });
      const pricing = await sitePricingService.getForSite(siteId);
      setSitePricing(prev => ({ ...prev, [siteId]: pricing }));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete pricing', variant: 'destructive' });
    }
  };

  // Attach to group
  const handleAttachToGroup = async () => {
    if (!editingCustomer || !selectedGroupId) return;
    
    setAttaching(true);
    try {
      await clientsService.attachToGroup(editingCustomer.id, parseInt(selectedGroupId));
      toast({ title: 'Success', description: 'Customer attached to group' });
      setAttachDialogOpen(false);
      closeCustomerEditDialog();
      loadClients();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to attach'), variant: 'destructive' });
    } finally {
      setAttaching(false);
    }
  };

  // Convert independent to group
  const handleConvertToGroup = async () => {
    if (!editingCustomer) return;
    
    setConverting(true);
    try {
      await clientsService.convertToGroup(editingCustomer.id);
      toast({ title: 'Success', description: 'Customer converted to group successfully' });
      setConvertDialogOpen(false);
      closeCustomerEditDialog();
      loadClients();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to convert'), variant: 'destructive' });
    } finally {
      setConverting(false);
    }
  };

  // Delete customer
  const handleDeleteCustomer = async () => {
    if (!deleteTarget) return;
    
    try {
      await clientsService.delete(deleteTarget.id);
      toast({ title: 'Success', description: 'Customer deleted' });
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      // Clear independent customer dialog state if open
      setIndependentCustomer(null);
      setIndependentDialogOpen(false);
      closeCustomerEditDialog();
      loadClients();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    }
  };

  // Batch selection handlers
  const toggleCustomerSelection = (customerId, e) => {
    e.stopPropagation();
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const toggleSiteSelection = (siteId, e) => {
    e.stopPropagation();
    setSelectedSites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  const selectAllCustomers = () => {
    if (selectedCustomers.size === filteredClients.length) {
      setSelectedCustomers(new Set());
    } else {
      setSelectedCustomers(new Set(filteredClients.map(c => c.id)));
    }
  };

  const clearSelection = () => {
    setSelectedCustomers(new Set());
    setSelectedSites(new Set());
  };

  const openBatchAction = (actionType) => {
    setBatchActionType(actionType);
    setBatchActionValue(actionType === 'status' ? true : '');
    setBatchActionDialogOpen(true);
  };

  const handleBatchAction = async () => {
    if (selectedCustomers.size === 0 && selectedSites.size === 0) return;
    
    setBatchProcessing(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      // Process customers
      for (const customerId of selectedCustomers) {
        try {
          const updateData = {};
          if (batchActionType === 'status') {
            updateData.is_active = batchActionValue;
          }
          await clientsService.update(customerId, updateData);
          
          // For independent customers, also update their site
          const customer = clients.find(c => c.id === customerId);
          if (customer?.client_kind === 'independent') {
            // Find and update the associated site
            const allSites = await sitesService.list({ region, limit: 500 });
            const customerSite = allSites.find(s => s.independent_client_id === customerId);
            if (customerSite) {
              const siteUpdateData = {};
              if (batchActionType === 'status') {
                siteUpdateData.is_active = batchActionValue;
              } else if (batchActionType === 'frequency') {
                siteUpdateData.audit_frequency = batchActionValue || null;
                siteUpdateData.custom_frequency_days = batchActionValue === 'custom' && batchCustomDays ? parseInt(batchCustomDays) : null;
              }
              await sitesService.update(customerSite.id, siteUpdateData);
            }
          }
          successCount++;
        } catch (err) {
          console.error(`Failed to update customer ${customerId}:`, err);
          errorCount++;
        }
      }

      // Process sites (for group customers)
      for (const siteId of selectedSites) {
        try {
          const updateData = {};
          if (batchActionType === 'status') {
            updateData.is_active = batchActionValue;
          } else if (batchActionType === 'frequency') {
            updateData.audit_frequency = batchActionValue || null;
            updateData.custom_frequency_days = batchActionValue === 'custom' && batchCustomDays ? parseInt(batchCustomDays) : null;
          }
          await sitesService.update(siteId, updateData);
          successCount++;
        } catch (err) {
          console.error(`Failed to update site ${siteId}:`, err);
          errorCount++;
        }
      }

      // Update local state
      if (batchActionType === 'status') {
        setClients(prev => prev.map(c => 
          selectedCustomers.has(c.id) ? { ...c, is_active: batchActionValue } : c
        ));
        // Update expanded group sites
        setExpandedGroupSites(prev => {
          const updated = { ...prev };
          for (const groupId of Object.keys(updated)) {
            updated[groupId] = updated[groupId]?.map(s => 
              selectedSites.has(s.id) ? { ...s, is_active: batchActionValue } : s
            );
          }
          return updated;
        });
      }

      setBatchActionDialogOpen(false);
      clearSelection();
      
      if (errorCount === 0) {
        toast({ title: 'Success', description: `Updated ${successCount} item${successCount !== 1 ? 's' : ''}` });
      } else {
        toast({ 
          title: 'Partial Success', 
          description: `Updated ${successCount}, failed ${errorCount}`, 
          variant: 'warning' 
        });
      }

      // Reload to ensure consistency
      loadClients();
    } catch (err) {
      toast({ title: 'Error', description: 'Batch update failed', variant: 'destructive' });
    } finally {
      setBatchProcessing(false);
    }
  };

  // Group expansion
  const toggleGroupExpand = async (clientId) => {
    const isExpanded = expandedGroups.has(clientId);
    
    if (isExpanded) {
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(clientId);
        return newSet;
      });
    } else {
      setExpandedGroups(prev => new Set([...prev, clientId]));
      if (!expandedGroupSites[clientId]) {
        setLoadingGroupSites(prev => new Set([...prev, clientId]));
        try {
          const sites = await clientsService.getSites(clientId);
          setExpandedGroupSites(prev => ({ ...prev, [clientId]: sites }));
        } catch (err) {
          toast({ title: 'Error', description: 'Failed to load sites', variant: 'destructive' });
        } finally {
          setLoadingGroupSites(prev => {
            const newSet = new Set(prev);
            newSet.delete(clientId);
            return newSet;
          });
        }
      }
    }
  };

  // Search/filter
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  
  // Status filter function
  const filterByStatus = useCallback((items) => {
    if (statusFilter === 'all') return items;
    if (statusFilter === 'active') return items.filter(c => c.is_active);
    if (statusFilter === 'inactive') return items.filter(c => !c.is_active);
    return items;
  }, [statusFilter]);
  
  // Enhanced filter that tracks which sites matched for each client
  const filteredClientsWithMatches = useMemo(() => {
    // First apply status filter
    const statusFiltered = filterByStatus(clients);
    
    if (!debouncedSearchQuery.trim()) {
      return statusFiltered.map(c => ({ ...c, matchedSites: [] }));
    }
    const query = debouncedSearchQuery.toLowerCase();
    return statusFiltered.map(client => {
      const matchedSites = [];
      let matches = false;
      
      // Search across customer name
      if (client.name.toLowerCase().includes(query)) matches = true;
      // Search across contact name/email
      if (client.contact_name?.toLowerCase().includes(query)) matches = true;
      if (client.contact_email?.toLowerCase().includes(query)) matches = true;
      // Search across site names and track which ones matched
      if (client.site_names && client.site_names.length > 0) {
        client.site_names.forEach(siteName => {
          if (siteName.toLowerCase().includes(query)) {
            matchedSites.push(siteName);
            matches = true;
          }
        });
      }
      // Search across city
      if (client.city?.toLowerCase().includes(query)) matches = true;
      
      return matches ? { ...client, matchedSites } : null;
    }).filter(Boolean);
  }, [clients, debouncedSearchQuery, filterByStatus]);
  
  const filteredClients = filteredClientsWithMatches;

  // Auto-expand groups that have matched sites when searching
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) return;
    
    const groupsWithMatchedSites = filteredClientsWithMatches
      .filter(c => c.client_kind === 'group' && c.matchedSites && c.matchedSites.length > 0)
      .map(c => c.id);
    
    if (groupsWithMatchedSites.length > 0) {
      // Auto-expand groups with matched sites
      groupsWithMatchedSites.forEach(async (clientId) => {
        if (!expandedGroups.has(clientId)) {
          setExpandedGroups(prev => new Set([...prev, clientId]));
          // Load sites if not already loaded
          if (!expandedGroupSites[clientId]) {
            setLoadingGroupSites(prev => new Set([...prev, clientId]));
            try {
              const sites = await clientsService.getSites(clientId);
              setExpandedGroupSites(prev => ({ ...prev, [clientId]: sites }));
            } catch (err) {
              console.error('Failed to auto-load sites:', err);
            } finally {
              setLoadingGroupSites(prev => {
                const newSet = new Set(prev);
                newSet.delete(clientId);
                return newSet;
              });
            }
          }
        }
      });
    }
  }, [debouncedSearchQuery, filteredClientsWithMatches]);

  const groupClientsList = filteredClients.filter(c => c.client_kind === 'group');
  const independentClientsList = filteredClients.filter(c => c.client_kind === 'independent');

  // Check if colour is used
  const isColourUsed = (hex) => {
    if (editingCustomer?.colour === hex) return false;
    return usedColours.includes(hex);
  };

  // Handle colour selection with duplicate check
  const handleColourSelect = async (colourHex, targetForm) => {
    if (!colourHex) {
      if (targetForm === 'customer') {
        setCustomerForm(prev => ({ ...prev, colour: null }));
      } else if (targetForm === 'new') {
        setNewCustomerForm(prev => ({ ...prev, colour: null }));
      }
      return;
    }
    
    // Check if colour is already used
    try {
      const excludeId = targetForm === 'customer' ? editingCustomer?.id : null;
      const result = await clientsService.checkColour(colourHex, excludeId);
      if (result.in_use) {
        // Show confirmation dialog
        setPendingColour(colourHex);
        setColourInUseBy(result.client_name);
        setColourTargetForm(targetForm);
        setColorConfirmDialogOpen(true);
      } else {
        // Colour is not in use, apply directly
        if (targetForm === 'customer') {
          setCustomerForm(prev => ({ ...prev, colour: colourHex }));
        } else if (targetForm === 'new') {
          setNewCustomerForm(prev => ({ ...prev, colour: colourHex }));
        }
      }
    } catch (err) {
      console.error('Failed to check colour:', err);
      // If check fails, still allow selection
      if (targetForm === 'customer') {
        setCustomerForm(prev => ({ ...prev, colour: colourHex }));
      } else if (targetForm === 'new') {
        setNewCustomerForm(prev => ({ ...prev, colour: colourHex }));
      }
    }
  };

  const confirmColourSelection = () => {
    if (pendingColour) {
      if (colourTargetForm === 'customer') {
        setCustomerForm(prev => ({ ...prev, colour: pendingColour }));
      } else if (colourTargetForm === 'new') {
        setNewCustomerForm(prev => ({ ...prev, colour: pendingColour }));
      }
    }
    setColorConfirmDialogOpen(false);
    setPendingColour(null);
    setColourInUseBy(null);
    setColourTargetForm(null);
  };

  // Render colour picker - now with duplicate warning
  const renderColourPicker = (currentColour, targetForm) => (
    <div className="space-y-2">
      <Label>Calendar Colour</Label>
      <div className="flex flex-wrap gap-2">
        {presetColours.map(colour => (
          <button
            key={colour.hex}
            type="button"
            onClick={() => handleColourSelect(colour.hex, targetForm)}
            className={`w-7 h-7 rounded-full border-2 transition-all relative ${
              currentColour === colour.hex ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'
            }`}
            style={{ backgroundColor: colour.hex }}
            title={colour.name}
          >
            {isColourUsed(colour.hex) && (
              <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">•</span>
            )}
          </button>
        ))}
        {currentColour && (
          <button
            type="button"
            onClick={() => handleColourSelect(null, targetForm)}
            className="w-7 h-7 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center hover:bg-muted"
            title="Remove colour"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );

  // ========== LIST VIEW ==========
  const renderListView = () => (
    <div className="space-y-6" data-testid="customers-list-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" data-testid="customers-title">Customers</h1>
          <p className="text-muted-foreground">Manage clients and their sites</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="add-customer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Customer
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers & sites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="customer-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active Only</SelectItem>
            <SelectItem value="inactive">Inactive Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All ({filteredClients.length})</TabsTrigger>
            <TabsTrigger value="groups">Groups ({groupClientsList.length})</TabsTrigger>
            <TabsTrigger value="independent">Independent ({independentClientsList.length})</TabsTrigger>
          </TabsList>
          
          {/* Batch Action Toolbar */}
          {(selectedCustomers.size > 0 || selectedSites.size > 0) && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-blue-700">
                <span className="font-medium">
                  {selectedCustomers.size + selectedSites.size} item{selectedCustomers.size + selectedSites.size !== 1 ? 's' : ''} selected
                </span>
                <Button variant="ghost" size="sm" onClick={clearSelection} className="h-7 text-blue-600 hover:text-blue-800">
                  <X className="h-3 w-3 mr-1" /> Clear
                </Button>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => openBatchAction('status')}
                  data-testid="batch-status-btn"
                >
                  Set Status
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => openBatchAction('frequency')}
                  data-testid="batch-frequency-btn"
                >
                  Set Frequency
                </Button>
              </div>
            </div>
          )}
          
          <TabsContent value="all" className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox 
                          checked={filteredClients.length > 0 && selectedCustomers.size === filteredClients.length}
                          onCheckedChange={selectAllCustomers}
                          data-testid="select-all-customers"
                        />
                      </TableHead>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sites</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map(client => (
                      <React.Fragment key={client.id}>
                        <TableRow 
                          className={`cursor-pointer hover:bg-muted/50 ${!client.is_active ? 'opacity-60' : ''} ${selectedCustomers.has(client.id) ? 'bg-blue-50' : ''}`}
                          onClick={() => openCustomerEdit(client)}
                          data-testid={`customer-row-${client.id}`}
                        >
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Checkbox 
                              checked={selectedCustomers.has(client.id)}
                              onCheckedChange={(checked) => toggleCustomerSelection(client.id, { stopPropagation: () => {} })}
                              data-testid={`select-customer-${client.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            {client.client_kind === 'group' && client.site_count > 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => { e.stopPropagation(); toggleGroupExpand(client.id); }}
                                data-testid={`expand-group-${client.id}-btn`}
                              >
                                {loadingGroupSites.has(client.id) ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : expandedGroups.has(client.id) ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-3">
                                {client.colour && (
                                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: client.colour }} />
                                )}
                                <span className="font-medium">{client.name}</span>
                              </div>
                              {/* Show matched sites when searching */}
                              {client.matchedSites && client.matchedSites.length > 0 && (
                                <div className="flex flex-wrap gap-1 pl-6">
                                  {client.matchedSites.slice(0, 3).map((siteName, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                      <MapPin className="h-2 w-2 mr-1" />{siteName}
                                    </Badge>
                                  ))}
                                  {client.matchedSites.length > 3 && (
                                    <Badge variant="outline" className="text-xs">+{client.matchedSites.length - 3} more</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={client.client_kind === 'group' ? 'default' : 'secondary'}>
                              {client.client_kind === 'group' ? <><Building2 className="h-3 w-3 mr-1" />Group</> : <><MapPin className="h-3 w-3 mr-1" />Independent</>}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={client.is_active ? 'outline' : 'secondary'} className={client.is_active ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-500'}>
                              {client.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>{client.site_count || 0} site{(client.site_count || 0) !== 1 ? 's' : ''}</TableCell>
                          <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => openCustomerEdit(client)}>
                              <Pencil className="h-4 w-4 mr-1" /> Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                        
                        {client.client_kind === 'group' && expandedGroups.has(client.id) && (
                          <>
                            {loadingGroupSites.has(client.id) ? (
                              <TableRow>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell colSpan={5} className="py-4">
                                  <div className="flex items-center gap-2 text-muted-foreground pl-4">
                                    <Loader2 className="h-4 w-4 animate-spin" /> Loading sites...
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : expandedGroupSites[client.id]?.map(site => {
                              const isMatched = client.matchedSites?.includes(site.name);
                              return (
                              <TableRow 
                                key={`site-${site.id}`} 
                                className={`cursor-pointer ${isMatched ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-l-blue-400' : 'bg-slate-50 hover:bg-slate-100'} ${!site.is_active ? 'opacity-60' : ''} ${selectedSites.has(site.id) ? 'bg-blue-100' : ''}`}
                                data-testid={`child-site-row-${site.id}`}
                                onClick={() => openSiteForDirectEdit(client, site)}
                              >
                                <TableCell onClick={e => e.stopPropagation()}>
                                  <Checkbox 
                                    checked={selectedSites.has(site.id)}
                                    onCheckedChange={(checked) => toggleSiteSelection(site.id, { stopPropagation: () => {} })}
                                    data-testid={`select-site-${site.id}`}
                                  />
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-3 pl-6">
                                    <ArrowRight className={`h-3 w-3 ${isMatched ? 'text-blue-500' : 'text-muted-foreground'}`} />
                                    <span className="text-sm">
                                      <span className="text-muted-foreground">{client.name} → </span>
                                      <span className={`font-medium ${isMatched ? 'text-blue-700' : ''}`}>{site.name}</span>
                                      {isMatched && <Badge className="ml-2 text-[10px] py-0" variant="secondary">Match</Badge>}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell><Badge variant="outline" className="text-xs"><MapPin className="h-3 w-3 mr-1" />Site</Badge></TableCell>
                                <TableCell>
                                  <Badge variant={site.is_active ? 'outline' : 'secondary'} className={`text-xs ${site.is_active ? 'text-green-600 border-green-200 bg-green-50' : 'text-gray-500'}`}>
                                    {site.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">{site.city || '-'}</TableCell>
                                <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                                  <Button variant="ghost" size="sm" onClick={() => openSiteForDirectEdit(client, site)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );})}
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    {filteredClients.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          {searchQuery ? 'No customers match your search' : 'No customers yet. Add your first customer to get started.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="groups" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" />Group Clients</CardTitle>
                <CardDescription>Commercial entities with multiple sites</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Sites</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupClientsList.map(client => (
                      <TableRow key={client.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openCustomerEdit(client)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {client.colour && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: client.colour }} />}
                            <span className="font-medium">{client.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>{client.site_count || 0} sites</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/customers/${client.id}/sub-regions`); }}
                            data-testid={`sub-regions-btn-${client.id}`}>
                            <Layers className="h-4 w-4 mr-1 text-violet-600" /> Sub-Regions
                          </Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openCustomerEdit(client); }}>
                            <Pencil className="h-4 w-4 mr-1" /> Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {groupClientsList.length === 0 && (
                      <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No group clients</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="independent" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" />Independent Clients</CardTitle>
                <CardDescription>Single-site businesses</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {independentClientsList.map(client => (
                      <TableRow key={client.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openCustomerEdit(client)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {client.colour && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: client.colour }} />}
                            <span className="font-medium">{client.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openCustomerEdit(client); }}>
                            <Pencil className="h-4 w-4 mr-1" /> Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {independentClientsList.length === 0 && (
                      <TableRow><TableCell colSpan={2} className="text-center py-8 text-muted-foreground">No independent clients</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );

  return (
    <div data-testid="customers-page">
      <Toaster />
      
      {renderListView()}

      {/* Customer Edit Dialog - Unified */}
      <Dialog open={customerEditDialogOpen} onOpenChange={(open) => { if (!open) closeCustomerEditDialog(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {customerForm.colour && <div className="w-5 h-5 rounded-full" style={{ backgroundColor: customerForm.colour }} />}
              <div>
                <DialogTitle data-testid="customer-edit-dialog-title">
                  {editingCustomer?.name || 'Edit Customer'}
                </DialogTitle>
                <DialogDescription>
                  <Badge variant={editingCustomer?.client_kind === 'group' ? 'default' : 'secondary'} className="mt-1">
                    {editingCustomer?.client_kind === 'group' ? 'Group' : 'Independent'}
                  </Badge>
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {loadingEditData ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Customer Details Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" /> Customer Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={customerForm.name || ''}
                        onChange={(e) => setCustomerForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Customer name"
                        data-testid="edit-customer-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <div className="flex items-center gap-3 h-10">
                        <Switch
                          checked={customerForm.is_active !== false}
                          onCheckedChange={(checked) => setCustomerForm(prev => ({ ...prev, is_active: checked }))}
                          data-testid="customer-active-toggle"
                        />
                        <span className={`text-sm ${customerForm.is_active !== false ? 'text-green-600' : 'text-muted-foreground'}`}>
                          {customerForm.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    {/* AP applicability — single business control for this client.
                        off = disabled (default for all new/unset clients);
                        on  = AP enabled; sites may still be individually excluded. */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5">
                        Action Plan
                      </Label>
                      <div className="flex items-center gap-3 h-10">
                        <Switch
                          checked={customerForm.ap_applicability_enabled === true}
                          onCheckedChange={(checked) => {
                            setCustomerForm(prev => ({ ...prev, ap_applicability_enabled: checked }));
                            if (checked && editingCustomer?.id) {
                              loadApExclusions(editingCustomer.id);
                              if (apRolloutMode === 'selected_only') loadApInclusions(editingCustomer.id);
                            } else if (!checked) {
                              setApExclusions([]);
                              setApInclusions([]);
                            }
                          }}
                          data-testid="customer-ap-enabled-toggle"
                        />
                        <span className={`text-sm ${customerForm.ap_applicability_enabled ? 'text-amber-700' : 'text-muted-foreground'}`}>
                          {customerForm.ap_applicability_enabled ? 'AP enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>

                    {/* D3 — AP Rollout section (Scheduler/Admin only; visible when AP is enabled) */}
                    {customerForm.ap_applicability_enabled && (isAdmin || isScheduler) && (
                      <div
                        className="col-span-2 space-y-3 border rounded-md p-3 bg-amber-50/50"
                        data-testid="ap-rollout-panel"
                      >
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
                            <AlertCircle className="h-3.5 w-3.5" />
                            AP Rollout
                          </Label>
                          <span className="text-xs text-muted-foreground">Group ON overrides nothing; Group OFF disables all modes</span>
                        </div>

                        {/* Mode selector */}
                        <div className="flex items-center gap-6" data-testid="ap-rollout-mode-selector">
                          <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="ap_rollout_mode"
                              value="all_except"
                              checked={apRolloutMode === 'all_except'}
                              onChange={() => {
                                setApRolloutMode('all_except');
                                saveApRolloutMode(editingCustomer?.id, 'all_except');
                              }}
                              data-testid="ap-rollout-mode-all-except"
                            />
                            <span>All sites, except…</span>
                            <span className="text-xs text-muted-foreground">(newly added sites are AP-eligible by default)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="ap_rollout_mode"
                              value="selected_only"
                              checked={apRolloutMode === 'selected_only'}
                              onChange={() => {
                                setApRolloutMode('selected_only');
                                saveApRolloutMode(editingCustomer?.id, 'selected_only');
                                loadApInclusions(editingCustomer?.id);
                              }}
                              data-testid="ap-rollout-mode-selected-only"
                            />
                            <span>Selected sites only</span>
                            <span className="text-xs text-muted-foreground">(new sites stay disabled until ticked)</span>
                          </label>
                        </div>

                        {/* all_except — show exclusion list */}
                        {apRolloutMode === 'all_except' && (
                          <div className="space-y-2" data-testid="ap-exclusions-section">
                            <p className="text-xs font-medium text-amber-700">Sites excluded from AP:</p>
                            {apExclusionsLoading ? (
                              <p className="text-xs text-muted-foreground">Loading…</p>
                            ) : apExclusions.length === 0 ? (
                              <p className="text-xs text-muted-foreground italic">No exclusions — all sites are AP-applicable.</p>
                            ) : (
                              <ul className="space-y-1 max-h-32 overflow-y-auto">
                                {apExclusions.map(exc => (
                                  <li key={exc.id} className="flex items-center justify-between text-sm bg-white rounded px-2 py-1 border">
                                    <span>{exc.site_name || `Site #${exc.site_id}`}</span>
                                    <button
                                      type="button"
                                      data-testid={`ap-exclusion-remove-${exc.id}`}
                                      onClick={() => removeApExclusion(editingCustomer?.id, exc.id)}
                                      className="text-red-500 hover:text-red-700 p-0.5 ml-2"
                                    >×</button>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {sites.length > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                <Select value={apExclusionSiteId} onValueChange={setApExclusionSiteId}>
                                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="ap-exclusion-site-select">
                                    <SelectValue placeholder="Select site to exclude…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {sites
                                      .filter(s => !apExclusions.some(exc => exc.site_id === s.id))
                                      .map(s => (
                                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  type="button" size="sm" variant="outline"
                                  data-testid="ap-exclusion-add-btn"
                                  onClick={() => addApExclusion(editingCustomer?.id, apExclusionSiteId)}
                                  disabled={!apExclusionSiteId}
                                  className="h-8 text-xs"
                                >Exclude</Button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* selected_only — searchable site checklist */}
                        {apRolloutMode === 'selected_only' && (
                          <div className="space-y-2" data-testid="ap-inclusions-section">
                            <p className="text-xs font-medium text-amber-700">
                              Sites included in AP:
                              <span className="font-normal text-muted-foreground ml-1">
                                ({apInclusions.length} of {sites.length} ticked)
                              </span>
                            </p>
                            {/* Search box */}
                            <input
                              type="text"
                              placeholder="Search sites…"
                              value={apSiteSearch}
                              onChange={e => setApSiteSearch(e.target.value)}
                              className="w-full text-xs border rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-amber-400"
                              data-testid="ap-inclusion-site-search"
                            />
                            {apInclusionsLoading ? (
                              <p className="text-xs text-muted-foreground">Loading…</p>
                            ) : (
                              <ul className="space-y-1 max-h-48 overflow-y-auto border rounded bg-white p-2">
                                {sites
                                  .filter(s => !apSiteSearch || s.name.toLowerCase().includes(apSiteSearch.toLowerCase()))
                                  .map(s => {
                                    const incl = apInclusions.find(i => i.site_id === s.id);
                                    return (
                                      <li key={s.id} className="flex items-center gap-2 text-sm px-1 py-0.5 rounded hover:bg-amber-50">
                                        <input
                                          type="checkbox"
                                          checked={!!incl}
                                          onChange={async () => {
                                            if (incl) {
                                              await removeApInclusion(editingCustomer?.id, incl.id);
                                            } else {
                                              await addApInclusion(editingCustomer?.id, s.id);
                                            }
                                          }}
                                          data-testid={`ap-inclusion-check-${s.id}`}
                                          className="accent-amber-600"
                                        />
                                        <span className={incl ? 'font-medium text-amber-800' : 'text-muted-foreground'}>{s.name}</span>
                                      </li>
                                    );
                                  })}
                                {sites.filter(s => !apSiteSearch || s.name.toLowerCase().includes(apSiteSearch.toLowerCase())).length === 0 && (
                                  <li className="text-xs text-muted-foreground italic px-1">No sites match "{apSiteSearch}"</li>
                                )}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Address Line 1</Label>
                      <Input
                        value={customerForm.address_line_1 || ''}
                        onChange={(e) => setCustomerForm(prev => ({ ...prev, address_line_1: e.target.value }))}
                        placeholder="Street address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address Line 2</Label>
                      <Input
                        value={customerForm.address_line_2 || ''}
                        onChange={(e) => setCustomerForm(prev => ({ ...prev, address_line_2: e.target.value }))}
                        placeholder="Suite, building, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={customerForm.city || ''}
                        onChange={(e) => setCustomerForm(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Postcode</Label>
                      <Input
                        value={customerForm.postcode || ''}
                        onChange={(e) => setCustomerForm(prev => ({ ...prev, postcode: e.target.value }))}
                        placeholder="Postcode"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Select value={customerForm.country || 'United Kingdom'} onValueChange={(v) => setCustomerForm(prev => ({ ...prev, country: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {renderColourPicker(customerForm.colour, 'customer')}
                  
                  {/* Customer Logo */}
                  {canManageLogos && (
                    <div className="space-y-2 pt-2">
                      <Label className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                        <ImageIcon className="h-4 w-4" /> Customer Logo
                      </Label>
                      <div className="flex items-center gap-4">
                        {editingCustomer?.logo_url ? (
                          <div className="relative group">
                            <StoredLogo
                              logoKey={editingCustomer.logo_url}
                              alt="Customer logo"
                              className="h-16 w-auto max-w-[160px] object-contain border rounded p-1 bg-white"
                              data-testid="customer-logo-preview"
                            />
                            <Button 
                              size="icon" variant="destructive"
                              className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={async () => {
                                if (!window.confirm('Remove this logo?')) return;
                                try {
                                  await api.delete(`/clients/${editingCustomer.id}/logo`);
                                  setEditingCustomer(prev => ({ ...prev, logo_url: null }));
                                  toast({ title: 'Logo removed' });
                                } catch (err) { toast({ title: 'Failed to remove logo', variant: 'destructive' }); }
                              }}
                              data-testid="remove-customer-logo"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <label className="cursor-pointer">
                              <input 
                                type="file" 
                                accept=".png,.jpg,.jpeg,.svg,.webp"
                                className="hidden"
                                data-testid="upload-customer-logo-input"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (file.size > 5 * 1024 * 1024) { toast({ title: 'File too large (max 5MB)', variant: 'destructive' }); return; }
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  try {
                                    const res = await api.post(`/clients/${editingCustomer.id}/logo`, formData);
                                    setEditingCustomer(prev => ({ ...prev, logo_url: res.data.logo_url }));
                                    toast({ title: 'Logo uploaded successfully' });
                                  } catch (err) { toast({ title: getApiError(err, 'Upload failed'), variant: 'destructive' }); }
                                  e.target.value = '';
                                }}
                              />
                              <Button variant="outline" size="sm" asChild>
                                <span data-testid="upload-customer-logo-btn"><Upload className="h-3 w-3 mr-1" /> Upload Logo</span>
                              </Button>
                            </label>
                            <span className="text-xs text-muted-foreground">PNG, JPG, SVG (max 5MB)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Contacts Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" /> Contacts ({contacts.length})
                    </h4>
                    <Button size="sm" variant="outline" onClick={() => openContactDialog()} data-testid="add-contact-btn">
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                  {contacts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No contacts yet</p>
                  ) : (
                    <div className="space-y-2">
                      {contacts.map(contact => (
                        <div key={contact.id} className="flex items-center justify-between p-2 border rounded" data-testid={`contact-row-${contact.id}`}>
                          <div className="flex items-center gap-3">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium text-sm">{contact.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {[contact.role, contact.email, contact.phone].filter(Boolean).join(' • ')}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openContactDialog(contact); }}><Pencil className="h-3 w-3" /></Button>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleDeleteContact(contact); }} data-testid={`delete-contact-${contact.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Sites & Pricing Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" /> Sites ({sites.length})
                    </h4>
                    {editingCustomer?.client_kind === 'group' && canManageSites && (
                      <Button size="sm" variant="outline" onClick={() => openSiteDialog()} data-testid="add-site-dialog-btn">
                        <Plus className="h-3 w-3 mr-1" /> Add Site
                      </Button>
                    )}
                  </div>
                  {sites.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No sites found</p>
                  ) : (
                    <div className="space-y-3">
                      {sites.map(site => (
                        <div key={site.id} className="border rounded p-3 space-y-2" data-testid={`site-section-${site.id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{site.name}</div>
                              <div className="text-xs text-muted-foreground">{[site.city, site.postcode].filter(Boolean).join(', ') || 'No address'}</div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openSiteDialog(site)} data-testid={`edit-site-${site.id}-btn`}>
                                <Pencil className="h-3 w-3 mr-1" /> Edit
                              </Button>
                              {editingCustomer?.client_kind === 'group' && (
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteSite(site)}>
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {/* Pricing preview - hidden from auditor-only users */}
                          {canEditPricing && sitePricing[site.id] && sitePricing[site.id].length > 0 && (
                            <div className="text-xs text-muted-foreground pl-2 border-l-2">
                              {sitePricing[site.id].slice(0, 2).map(p => (
                                <div key={p.id}>{p.service_name}: {formatCurrency(p.price)}</div>
                              ))}
                              {sitePricing[site.id].length > 2 && <div>+{sitePricing[site.id].length - 2} more</div>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex justify-between items-center">
            <div className="flex gap-2">
              {/* Attach to Group - only for independent customers with available groups, hidden for auditor-only */}
              {!isAuditorOnly && editingCustomer?.client_kind === 'independent' && groupClients.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setAttachDialogOpen(true)}>
                  <Link2 className="h-4 w-4 mr-1" /> Attach to Group
                </Button>
              )}
              {/* Convert to Group - only for independent customers, hidden for auditor-only */}
              {!isAuditorOnly && editingCustomer?.client_kind === 'independent' && (
                <Button variant="outline" size="sm" onClick={() => setConvertDialogOpen(true)}>
                  <Building2 className="h-4 w-4 mr-1" /> Convert to Group
                </Button>
              )}
              {/* Delete - hidden for auditor-only */}
              {!isAuditorOnly && (
                <Button variant="destructive" size="sm" onClick={() => { setDeleteTarget(editingCustomer); setDeleteDialogOpen(true); }}>
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeCustomerEditDialog}>Close</Button>
              <Button onClick={handleSaveCustomer} disabled={saving} data-testid="save-customer-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Save className="h-4 w-4 mr-2" /> Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Customer Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
            <DialogDescription>Create a new customer. For independent clients, a site will be auto-created.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newCustomerForm.name}
                  onChange={(e) => setNewCustomerForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Customer name"
                  data-testid="new-customer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={newCustomerForm.client_kind} onValueChange={(v) => setNewCustomerForm(prev => ({ ...prev, client_kind: v }))}>
                  <SelectTrigger data-testid="new-customer-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="independent"><div className="flex items-center gap-2"><MapPin className="h-4 w-4" />Independent</div></SelectItem>
                    <SelectItem value="group"><div className="flex items-center gap-2"><Building2 className="h-4 w-4" />Group</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address Line 1 *</Label>
              <Input
                value={newCustomerForm.address_line_1}
                onChange={(e) => setNewCustomerForm(prev => ({ ...prev, address_line_1: e.target.value }))}
                placeholder="Street address"
              />
            </div>
            <div className="space-y-2">
              <Label>Address Line 2</Label>
              <Input
                value={newCustomerForm.address_line_2}
                onChange={(e) => setNewCustomerForm(prev => ({ ...prev, address_line_2: e.target.value }))}
                placeholder="Suite, building, etc."
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Input
                  value={newCustomerForm.city}
                  onChange={(e) => setNewCustomerForm(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label>Postcode</Label>
                <Input
                  value={newCustomerForm.postcode}
                  onChange={(e) => setNewCustomerForm(prev => ({ ...prev, postcode: e.target.value }))}
                  placeholder="Postcode"
                />
              </div>
              <div className="space-y-2">
                <Label>Country *</Label>
                <Select value={newCustomerForm.country} onValueChange={(v) => setNewCustomerForm(prev => ({ ...prev, country: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {renderColourPicker(newCustomerForm.colour, 'new')}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomer} disabled={creatingCustomer} data-testid="create-customer-submit-btn">
              {creatingCustomer && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                data-testid="contact-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Input
                value={contactForm.role}
                onChange={(e) => setContactForm(prev => ({ ...prev, role: e.target.value }))}
                placeholder="e.g., Operations Manager"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={contactForm.phone}
                onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder={phonePlaceholder}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveContact} data-testid="save-contact-btn">
              {editingContact ? 'Update' : 'Add'} Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site Dialog - Unified with Pricing */}
      {/* Add Site Dialog — used when editingSite is null */}
      <Dialog open={siteDialogOpen && !editingSite} onOpenChange={(v) => { if (!v) setSiteDialogOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Add Site</DialogTitle>
            <DialogDescription>Create a new site for this customer.</DialogDescription>
          </DialogHeader>
          <Tabs value={siteDialogTab} onValueChange={setSiteDialogTab} className="w-full">
            <TabsContent value="profile" className="mt-0">
          <div className="grid gap-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 240px)' }}>
            {/* Site Details Section */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-4 w-4" /> Site Details
              </h4>
              <div className="flex gap-4">
                <div className="space-y-2 flex-1">
                  <Label>Site Name *</Label>
                  <Input
                    value={siteForm.name || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Site name"
                    data-testid="site-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="flex items-center gap-3 h-10">
                    <Switch
                      checked={siteForm.is_active !== false}
                      onCheckedChange={(checked) => setSiteForm(prev => ({ ...prev, is_active: checked }))}
                      data-testid="site-active-toggle"
                    />
                    <span className={`text-sm ${siteForm.is_active !== false ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {siteForm.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Address Line 1</Label>
                  <Input
                    value={siteForm.address_line_1 || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, address_line_1: e.target.value }))}
                    placeholder="Street address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address Line 2</Label>
                  <Input
                    value={siteForm.address_line_2 || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, address_line_2: e.target.value }))}
                    placeholder="Suite, building, etc."
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={siteForm.city || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Postcode</Label>
                  <Input
                    value={siteForm.postcode || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, postcode: e.target.value }))}
                    placeholder="Postcode"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select value={siteForm.country || 'United Kingdom'} onValueChange={(v) => setSiteForm(prev => ({ ...prev, country: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Audit Frequency</Label>
                  <Select value={siteForm.audit_frequency || 'not_set'} onValueChange={(v) => setSiteForm(prev => ({ ...prev, audit_frequency: v === 'not_set' ? null : v, custom_frequency_days: v !== 'custom' ? '' : prev.custom_frequency_days }))}>
                    <SelectTrigger data-testid="site-audit-frequency">
                      <SelectValue placeholder="Select frequency..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_set">Not Set</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="bi-monthly">Bi-Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="adhoc">Ad-hoc</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  {siteForm.audit_frequency === 'custom' && (
                    <div className="mt-2">
                      <Label className="text-xs text-muted-foreground">Days between audits</Label>
                      <Input
                        type="number"
                        min="1"
                        max="730"
                        placeholder="e.g. 45"
                        value={siteForm.custom_frequency_days || ''}
                        onChange={(e) => setSiteForm(prev => ({ ...prev, custom_frequency_days: e.target.value }))}
                        data-testid="site-custom-frequency-days"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Year End */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Year End</Label>
                <div className="flex gap-2">
                  <Select
                    value={siteForm.year_end_month || ''}
                    onValueChange={(v) => setSiteForm(prev => ({ ...prev, year_end_month: v, year_end_day: '' }))}
                  >
                    <SelectTrigger className="w-36" data-testid="site-year-end-month">
                      <SelectValue placeholder="Month..." />
                    </SelectTrigger>
                    <SelectContent>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                        <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={siteForm.year_end_day || ''}
                    onValueChange={(v) => setSiteForm(prev => ({ ...prev, year_end_day: v }))}
                    disabled={!siteForm.year_end_month}
                  >
                    <SelectTrigger className="w-24" data-testid="site-year-end-day">
                      <SelectValue placeholder="Day..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(siteForm.year_end_month || siteForm.year_end_day) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-9 px-2 text-xs text-muted-foreground hover:text-destructive hover:border-destructive"
                      onClick={() => setSiteForm(prev => ({ ...prev, year_end_month: '', year_end_day: '' }))}
                      data-testid="site-year-end-clear"
                      title="Clear year-end date"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />Clear
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Used for YTD calculations in stocktake reports</p>
              </div>
            </div>
            
            <Separator />
            
            {/* Budget/Stockholding Section */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                <Banknote className="h-4 w-4" /> Budget & Stockholding
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Beverages Budget COS (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={siteForm.budget_cost_of_sales_pct || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, budget_cost_of_sales_pct: e.target.value ? parseFloat(e.target.value) : null }))}
                    placeholder="e.g. 25.5"
                    data-testid="site-bev-budget-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Beverages Stockholding (Days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={siteForm.days_stockholding_period || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, days_stockholding_period: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="e.g. 14"
                    data-testid="site-bev-stockholding-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Food Budget COS (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={siteForm.food_budget_cost_of_sales_pct || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, food_budget_cost_of_sales_pct: e.target.value ? parseFloat(e.target.value) : null }))}
                    placeholder="e.g. 30.0"
                    data-testid="site-food-budget-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Food Stockholding (Days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={siteForm.food_days_stockholding_period || ''}
                    onChange={(e) => setSiteForm(prev => ({ ...prev, food_days_stockholding_period: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="e.g. 7"
                    data-testid="site-food-stockholding-input"
                  />
                </div>
              </div>
            </div>
          </div>
            </TabsContent>
          </Tabs>
          <DialogFooter className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
            <div className="flex gap-2 flex-wrap" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveSite} data-testid="save-site-btn">
                Add Site
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Site Dialog — shared with Budget page; full feature parity */}
      <SharedSiteEditDialog
        open={siteDialogOpen && !!editingSite}
        onOpenChange={(v) => { if (!v) { setSiteDialogOpen(false); setEditingSite(null); } }}
        siteId={editingSite?.id}
        siteName={editingSite?.name}
        customerName={editingCustomer?.name}
        onSaved={(updated) => {
          if (updated) {
            // Optimistically update local site list (preserve logo_url managed separately)
            setSites(prev => prev.map(s =>
              s.id === editingSite?.id ? { ...s, ...updated } : s
            ));
            if (editingCustomer?.client_kind === 'group') {
              setExpandedGroupSites(prev => ({
                ...prev,
                [editingCustomer.id]: prev[editingCustomer.id]?.map(s =>
                  s.id === editingSite?.id ? { ...s, ...updated } : s
                ),
              }));
            }
          }
          setSiteDialogOpen(false);
          setEditingSite(null);
        }}
        onDetached={() => {
          setSiteDialogOpen(false);
          setEditingSite(null);
          loadClients();
        }}
      />


      {/* Attach to Group Dialog */}
      <Dialog open={attachDialogOpen} onOpenChange={setAttachDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach to Group</DialogTitle>
            <DialogDescription>
              Move this independent customer's site to a group. The independent customer wrapper will be archived.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Group</Label>
            <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {groupClients.map(g => (
                  <SelectItem key={g.id} value={g.id.toString()}>
                    <div className="flex items-center gap-2">
                      {g.colour && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: g.colour }} />}
                      {g.name} ({g.site_count || 0} sites)
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAttachToGroup} disabled={!selectedGroupId || attaching}>
              {attaching && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Attach to Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Group Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Group</DialogTitle>
            <DialogDescription>
              Convert this independent customer to a group. This will allow you to add multiple sites to this customer.
              The existing site will remain as the first site under the group.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After conversion, you'll be able to add additional sites to "{editingCustomer?.name}".
                All existing job history and contacts will be preserved.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConvertToGroup} disabled={converting}>
              {converting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Convert to Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.name}" and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCustomer} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Batch Action Dialog */}
      <Dialog open={batchActionDialogOpen} onOpenChange={setBatchActionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {batchActionType === 'status' ? 'Set Status' : 'Set Audit Frequency'}
            </DialogTitle>
            <DialogDescription>
              Apply to {selectedCustomers.size + selectedSites.size} selected item{selectedCustomers.size + selectedSites.size !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {batchActionType === 'status' ? (
              <div className="space-y-3">
                <Label>Status</Label>
                <div className="flex gap-4">
                  <Button
                    variant={batchActionValue === true ? 'default' : 'outline'}
                    onClick={() => setBatchActionValue(true)}
                    className="flex-1"
                    data-testid="batch-set-active"
                  >
                    <span className="w-2 h-2 rounded-full bg-green-500 mr-2" />
                    Active
                  </Button>
                  <Button
                    variant={batchActionValue === false ? 'default' : 'outline'}
                    onClick={() => setBatchActionValue(false)}
                    className="flex-1"
                    data-testid="batch-set-inactive"
                  >
                    <span className="w-2 h-2 rounded-full bg-gray-400 mr-2" />
                    Inactive
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Label>Audit Frequency</Label>
                <Select value={batchActionValue || 'not_set'} onValueChange={(v) => { setBatchActionValue(v); if (v !== 'custom') setBatchCustomDays(''); }}>
                  <SelectTrigger data-testid="batch-frequency-select">
                    <SelectValue placeholder="Select frequency..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_set">Not Set</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="fortnightly">Fortnightly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="bi-monthly">Bi-Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="adhoc">Ad-hoc</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {batchActionValue === 'custom' && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Days between audits</Label>
                    <Input
                      type="number"
                      min="1"
                      max="730"
                      placeholder="e.g. 45"
                      value={batchCustomDays}
                      onChange={(e) => setBatchCustomDays(e.target.value)}
                      data-testid="batch-custom-frequency-days"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBatchAction} 
              disabled={batchProcessing || (batchActionType === 'status' && batchActionValue === null)}
              data-testid="batch-apply-btn"
            >
              {batchProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Apply to {selectedCustomers.size + selectedSites.size} item{selectedCustomers.size + selectedSites.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      
      {/* Independent Customer Dialog - Simplified view for independent customers */}
      <IndependentCustomerDialog
        open={independentDialogOpen}
        onOpenChange={setIndependentDialogOpen}
        customer={independentCustomer}
        usedColours={usedColours}
        onSaved={() => {
          setIndependentDialogOpen(false);
          setIndependentCustomer(null);
          loadClients();
        }}
        onDeleted={() => {
          if (independentCustomer) {
            setDeleteTarget(independentCustomer);
            setDeleteDialogOpen(true);
            setIndependentDialogOpen(false);
          }
        }}
      />

      {/* Duplicate Colour Warning Dialog */}
      <AlertDialog open={colorConfirmDialogOpen} onOpenChange={setColorConfirmDialogOpen}>
        <AlertDialogContent data-testid="colour-duplicate-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              Colour Already in Use
            </AlertDialogTitle>
            <AlertDialogDescription>
              The selected colour has already been assigned to client <span className="font-semibold text-foreground">"{colourInUseBy}"</span>. 
              <br /><br />
              Are you sure you want to proceed? Having multiple customers with the same colour may make them harder to distinguish on the calendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setColorConfirmDialogOpen(false); setPendingColour(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmColourSelection}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-colour-duplicate-btn"
            >
              Use Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
    </div>
  );
}
